<template>
  <div class="inventory-shell">
    <!-- HERO -->
    <section class="inventory-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Inventory</h1>
        <p class="hero-copy">
          Track what is currently in stock, what it cost, and what is ready to sell, pour, move, or adjust.
        </p>
      </div>
    </section>

    <!-- TOOLBAR -->
    <section class="inventory-toolbar">
      <div class="search-wrap">
        <span class="search-icon">⌕</span>
        <input
          type="text"
          v-model="filters.item_name"
          placeholder="Search item name..."
        />
      </div>

      <div class="search-wrap">
        <span class="search-icon">#</span>
        <input
          type="text"
          v-model="filters.item_no"
          placeholder="Item no..."
        />
      </div>

      <div class="search-wrap">
        <span class="search-icon">✦</span>
        <input
          type="text"
          v-model="filters.description"
          placeholder="Search description..."
        />
      </div>
    </section>

    <!-- INVENTORY CARDS -->
    <section v-if="sortedInventory.length > 0" class="inventory-grid">
      <article
        v-for="item in sortedInventory"
        :key="item.item_no"
        class="inventory-card"
        :class="{ empty: Number(item.quantity_on_hand || 0) === 0 }"
      >
        <div class="inventory-main">
          <div class="inventory-title-row">
            <div>
              <p class="label">Item #{{ item.item_no }}</p>
              <h2>{{ item.item_name || 'Unnamed Item' }}</h2>
            </div>

            <span
              class="status-pill"
              :class="Number(item.quantity_on_hand || 0) > 0 ? 'status-in-stock' : 'status-empty'"
            >
              {{ Number(item.quantity_on_hand || 0) > 0 ? 'In Stock' : 'Zero Stock' }}
            </span>
          </div>

          <div class="inventory-metrics">
            <div>
              <span>Quantity</span>
              <strong>{{ Number(item.quantity_on_hand || 0) }}</strong>
            </div>

            <div>
              <span>Moving Avg Cost</span>
              <strong>${{ Number(item.moving_avg_cost || 0).toFixed(2) }}</strong>
            </div>

            <div>
              <span>Category</span>
              <strong>{{ item.category_name || '—' }}</strong>
            </div>

            <div>
              <span>Last Sale</span>
              <strong>{{ formatDate(item.last_sale_date) }}</strong>
            </div>
          </div>

          <p class="inventory-note">
            {{ item.description || item.details || 'No description available.' }}
          </p>

          <div class="inventory-subdetails">
            <span>Updated {{ formatDate(item.last_updated) }}</span>
          </div>
        </div>

        <div class="card-actions">
          <button @click="openAdjustPopup(item)" class="primary-action small">
            Adjust
          </button>

          <button
            @click="confirmDelete(item)"
            class="danger-action"
            :class="{ disabled: Number(item.quantity_on_hand || 0) > 0 }"
          >
            Delete
          </button>
        </div>
      </article>
    </section>

    <!-- EMPTY STATE -->
    <section v-else class="empty-state">
      <p class="eyebrow">No Inventory</p>
      <h2>No inventory found.</h2>
      <p>Inventory will appear here after purchases are received or items are adjusted in.</p>
    </section>

    <!-- ADJUST INVENTORY MODAL -->
    <div v-if="showAdjustPopup" class="modal-overlay">
      <div class="inventory-modal small-modal">
        <div class="modal-topline"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Inventory Adjustment</p>
            <h2>Adjust Stock</h2>
            <p>
              Adjust quantity for <strong>{{ selectedItem.item_name }}</strong>.
            </p>
          </div>

          <button type="button" @click="showAdjustPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="form-panel">
            <div class="readonly-grid">
              <div>
                <span>Item No</span>
                <strong>{{ selectedItem.item_no }}</strong>
              </div>

              <div>
                <span>Current Qty</span>
                <strong>{{ Number(selectedItem.quantity_on_hand || 0) }}</strong>
              </div>
            </div>

            <label>
              <span>Quantity Change</span>
              <input
                type="number"
                v-model.number="adjustedQuantity"
                required
                placeholder="Example: 12 or -2"
              />
            </label>

            <p class="helper-text">
              Enter a positive number to add stock, or a negative number to reduce stock.
            </p>
          </section>

          <footer class="modal-actions">
            <button type="button" @click="showAdjustPopup = false" class="cancel-action">
              Cancel
            </button>

            <button type="button" @click="submitAdjustment" class="primary-action">
              Confirm Adjustment
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- DELETE INVENTORY MODAL -->
    <div v-if="showDeletePopup" class="modal-overlay">
      <div class="inventory-modal small-modal">
        <div class="modal-topline red"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Delete Inventory Row</p>
            <h2>Delete Item?</h2>
            <p>
              This will permanently remove inventory for
              <strong>{{ selectedItem.item_name || selectedItem.item_no }}</strong>.
            </p>
          </div>

          <button type="button" @click="showDeletePopup = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="form-panel danger-panel">
            <p>
              This action is permanent. Inventory can only be deleted when quantity on hand is zero.
            </p>
          </section>

          <footer class="modal-actions">
            <button type="button" @click="showDeletePopup = false" class="cancel-action">
              Cancel
            </button>

            <button type="button" @click="deleteItem" class="danger-action">
              Delete
            </button>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  data() {
    return {
      inventory: [],

      filters: {
        item_no: '',
        item_name: '',
        description: ''
      },

      showAdjustPopup: false,
      showDeletePopup: false,

      selectedItem: {},
      adjustedQuantity: 0
    };
  },

  computed: {
    sortedInventory() {
      return this.filteredInventory.slice().sort((a, b) =>
        String(a.item_name || '').localeCompare(String(b.item_name || ''))
      );
    },

    filteredInventory() {
      return this.inventory.filter(item => {
        const itemNo = String(item.item_no || '').toLowerCase();
        const itemName = String(item.item_name || '').toLowerCase();
        const description = String(item.description || item.details || '').toLowerCase();

        return (
          (!this.filters.item_no ||
            itemNo.includes(this.filters.item_no.toLowerCase())) &&
          (!this.filters.item_name ||
            itemName.includes(this.filters.item_name.toLowerCase())) &&
          (!this.filters.description ||
            description.includes(this.filters.description.toLowerCase()))
        );
      });
    }
  },

  mounted() {
    this.fetchInventory();
    console.log("✅ InventoryPage component mounted.");
  },

  methods: {
    async fetchInventory() {
      try {
        console.log("🔄 Fetching inventory...");

        const response = await api.get('/inventory');

        this.inventory = (response.data || []).map(item => ({
          item_no: item.item_no,
          item_name: item.item_name,
          description: item.description || item.details || "N/A",
          quantity_on_hand: Number(item.quantity_on_hand || 0),
          moving_avg_cost: Number(item.moving_avg_cost || 0),
          category_name: item.category_name || "Unknown",
          last_updated: item.last_updated || null,
          last_sale_date: item.last_sale_date || null
        }));

        console.log("📥 Inventory loaded:", this.inventory);
      } catch (error) {
        console.error("❌ Error fetching inventory:", error);
      }
    },

    openAdjustPopup(item) {
      this.selectedItem = { ...item };
      this.adjustedQuantity = 0;
      this.showAdjustPopup = true;

      this.$nextTick(() => {
        const input = document.querySelector(".inventory-modal input");
        if (input) input.focus();
      });
    },

    async submitAdjustment() {
      try {
        await api.patch(`/inventory/${this.selectedItem.item_no}`, {
          quantity_on_hand: this.adjustedQuantity
        });

        this.showAdjustPopup = false;
        await this.fetchInventory();
      } catch (error) {
        console.error('❌ Error adjusting inventory:', error);
        alert("Failed to adjust inventory. Check console for details.");
      }
    },

    confirmDelete(item) {
      console.log("🗑 Attempting to delete item:", item.item_no);

      if (Number(item.quantity_on_hand || 0) > 0) {
        alert(
          "❌ Cannot delete inventory while quantity on hand is greater than zero.\n\nAdjust the quantity to zero first."
        );
        return;
      }

      this.selectedItem = { ...item };
      this.showDeletePopup = true;
    },

    async deleteItem() {
      console.log("🗑 Deleting inventory item:", this.selectedItem.item_no);

      try {
        await api.delete(`/inventory/${this.selectedItem.item_no}`);

        this.showDeletePopup = false;
        await this.fetchInventory();
      } catch (error) {
        console.error("❌ Error deleting inventory:", error);
        alert("Failed to delete inventory. Check console for details.");
      }
    },

    formatDate(date) {
      if (!date || date === "N/A") return "—";

      const parsed = new Date(date);

      if (Number.isNaN(parsed.getTime())) return "—";

      return parsed.toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.inventory-shell {
  min-height: calc(100vh - 80px);
  padding: 28px;
  color: #f7efe8;
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

/* ---------- Hero ---------- */

.inventory-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 30px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.035));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.38);
  backdrop-filter: blur(18px);
}

.eyebrow {
  margin: 0 0 8px;
  color: #c8a66a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  font-weight: 900;
}

.inventory-hero h1 {
  margin: 0;
  font-size: clamp(40px, 7vw, 72px);
  line-height: 0.92;
  letter-spacing: -0.07em;
}

.hero-copy {
  max-width: 700px;
  margin: 16px 0 0;
  color: #cfc2bd;
  font-size: 16px;
  line-height: 1.5;
}

/* ---------- Toolbar ---------- */

.inventory-toolbar {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(160px, 0.45fr) minmax(220px, 0.7fr);
  gap: 14px;
  margin: 22px 0;
}

.search-wrap {
  min-height: 56px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background: rgba(12, 8, 12, 0.7);
  color: #f7efe8;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
}

.search-icon {
  color: #c8a66a;
  font-size: 22px;
  margin-right: 10px;
  line-height: 1;
}

.search-wrap input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: #f7efe8;
  font-size: 16px;
}

.search-wrap input::placeholder {
  color: #8f827e;
}

/* ---------- Inventory Cards ---------- */

.inventory-grid {
  display: grid;
  gap: 18px;
}

.inventory-card {
  border-radius: 28px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.3);
}

.inventory-card.empty {
  opacity: 0.72;
}

.inventory-main {
  padding: 24px;
}

.inventory-title-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.label {
  margin: 0 0 5px;
  color: #9b8f8b;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 900;
}

.inventory-title-row h2 {
  margin: 0;
  font-size: clamp(24px, 4vw, 34px);
  letter-spacing: -0.045em;
  line-height: 1.02;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
  white-space: nowrap;
}

.status-in-stock {
  background: rgba(47, 170, 118, 0.14);
  color: #76e0aa;
  border: 1px solid rgba(118, 224, 170, 0.28);
}

.status-empty {
  background: rgba(155, 45, 45, 0.22);
  color: #ffb4a8;
  border: 1px solid rgba(255, 118, 118, 0.24);
}

.inventory-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 22px;
}

.inventory-metrics div {
  min-height: 78px;
  padding: 15px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.inventory-metrics span {
  display: block;
  color: #978b86;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 900;
}

.inventory-metrics strong {
  display: block;
  margin-top: 8px;
  font-size: 15px;
  color: #fff8f1;
  overflow-wrap: anywhere;
}

.inventory-metrics div:first-child strong {
  font-size: 26px;
}

.inventory-note {
  margin: 14px 0 0;
  padding: 14px 16px;
  border-radius: 18px;
  color: #cfc2bd;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.inventory-subdetails {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.inventory-subdetails span {
  padding: 8px 12px;
  border-radius: 999px;
  color: #cfc2bd;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 13px;
  font-weight: 800;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 24px 24px;
}

/* ---------- Buttons ---------- */

.primary-action,
.cancel-action,
.danger-action {
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 0;
  color: #fff8f1;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition:
    transform 0.14s ease,
    box-shadow 0.14s ease,
    background 0.14s ease;
}

.primary-action {
  min-height: 58px;
  padding: 0 26px;
  background: linear-gradient(135deg, #8f123f, #c24a2f);
  box-shadow: 0 14px 32px rgba(194, 74, 47, 0.3);
}

.primary-action.small {
  min-height: 48px;
}

.cancel-action {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
}

.danger-action {
  background: rgba(155, 45, 45, 0.38);
  border: 1px solid rgba(255, 118, 118, 0.25);
  color: #ffb4a8;
}

.danger-action.disabled {
  opacity: 0.45;
}

.primary-action:hover,
.cancel-action:hover,
.danger-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
}

/* ---------- Empty ---------- */

.empty-state {
  padding: 38px;
  text-align: center;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px dashed rgba(255, 255, 255, 0.16);
  color: #cfc2bd;
}

.empty-state h2 {
  margin: 0 0 8px;
  color: #fff8f1;
  letter-spacing: -0.035em;
}

.empty-state p {
  margin: 0 0 18px;
}

/* ---------- Modal ---------- */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.inventory-modal {
  width: min(760px, 96vw);
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.11);
  color: #f7efe8;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
}

.small-modal {
  width: min(560px, 94vw);
}

.modal-topline {
  height: 5px;
  flex: 0 0 auto;
  background: linear-gradient(90deg, #8f123f, #c24a2f, #c8a66a);
}

.modal-topline.red {
  background: linear-gradient(90deg, #9b2d2d, #ff765f);
}

.modal-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: clamp(28px, 5vw, 44px);
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  overflow-y: auto;
  padding: 22px 28px 28px;
}

.form-panel {
  padding: 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 16px;
}

.danger-panel {
  border-color: rgba(255, 118, 118, 0.24);
  background: rgba(155, 45, 45, 0.14);
}

.readonly-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 16px;
}

.readonly-grid div {
  padding: 15px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.readonly-grid span,
.inventory-modal label span {
  display: block;
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.readonly-grid strong {
  display: block;
  margin-top: 8px;
  color: #fff8f1;
  font-size: 18px;
}

.inventory-modal label {
  display: grid;
  gap: 7px;
  color: #f7efe8;
}

.inventory-modal input {
  min-height: 52px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  outline: 0;
  background: rgba(6, 4, 6, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  font-size: 15px;
  font-family: inherit;
}

.inventory-modal input::placeholder {
  color: #766c68;
}

.inventory-modal input:focus {
  border-color: rgba(200, 166, 106, 0.65);
  box-shadow: 0 0 0 3px rgba(200, 166, 106, 0.14);
}

.helper-text {
  margin: 10px 0 0;
  color: #aaa09b;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 4px;
}

.modal-actions .primary-action,
.modal-actions .cancel-action,
.modal-actions .danger-action {
  min-width: 140px;
}

/* ---------- Responsive ---------- */

@media (max-width: 1000px) {
  .inventory-toolbar {
    grid-template-columns: 1fr;
  }

  .inventory-metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 720px) {
  .inventory-shell {
    padding: 16px;
  }

  .inventory-hero,
  .inventory-title-row,
  .modal-header {
    flex-direction: column;
  }

  .inventory-hero {
    align-items: stretch;
    padding: 24px;
  }

  .inventory-metrics,
  .readonly-grid {
    grid-template-columns: 1fr;
  }

  .card-actions,
  .modal-actions {
    flex-direction: column;
  }

  .primary-action,
  .cancel-action,
  .danger-action {
    width: 100%;
  }

  .modal-overlay {
    padding: 12px;
    align-items: stretch;
  }

  .inventory-modal,
  .small-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 24px;
  }

  .modal-header,
  .modal-body {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>